import { TextField } from "@mui/material";
import lodash from "lodash";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import {
  ThreeDPanelState,
  usePanelState,
  useVizDispatch,
} from "@/shared/state/visualization";
import { setThreeDPanelConfig } from "@/shared/state/visualization/actions";
import { Grid } from "@/shared/state/visualization/schema/v1/three-d";

import styles from "./SceneSettings.module.css";

interface GridSettingsProps {
  panelId: string;
}

export function GridSettings({ panelId }: GridSettingsProps) {
  const dispatch = useVizDispatch();
  const panelState = usePanelState(panelId) as ThreeDPanelState;
  const grid: Grid = React.useMemo(
    () => ({
      // Default values
      color: "#888888",
      scale: 1,
      verticalOffset: undefined,
      // Config values,
      ...panelState.config?.grid,
    }),
    [panelState.config?.grid],
  );

  const [rgbColor, setRGBColor] = React.useState(() => {
    // Color picker doesn't manage RGBA, only RGB.
    return grid.color.substring(0, 7);
  });
  const [scale, setScale] = React.useState(`${grid.scale}`);
  const [verticalOffset, setVerticalOffset] = React.useState(
    `${grid.verticalOffset ?? ""}`,
  );

  const [scaleError, setScaleError] = React.useState("");
  const [verticalOffsetError, setVerticalOffsetError] = React.useState("");

  React.useEffect(
    function onSettingChanged() {
      setScaleError("");
      const parsedScale = scale === "" ? undefined : Number.parseFloat(scale);
      if (parsedScale === undefined) {
        // noop
      } else if (!Number.isFinite(parsedScale)) {
        setScaleError("Scale must be a number");
        return;
      } else if (parsedScale < 0) {
        setScaleError("Scale must be positive");
        return;
      }
      setVerticalOffsetError("");
      const parsedVerticalOffset =
        verticalOffset === "" ? undefined : Number.parseFloat(verticalOffset);
      if (
        parsedVerticalOffset !== undefined &&
        Number.isNaN(parsedVerticalOffset)
      ) {
        setVerticalOffsetError("Vertical offset must be a number");
        return;
      }
      const updatedGrid: Grid = {
        color: `${rgbColor}88`, // Add alpha back
        scale: parsedScale,
        verticalOffset: parsedVerticalOffset,
      };
      if (!lodash.isEqual(grid, updatedGrid)) {
        dispatch(
          setThreeDPanelConfig(panelId, {
            grid: updatedGrid,
          }),
        );
      }
    },
    [rgbColor, scale, verticalOffset, dispatch, panelId, grid],
  );

  return (
    <div className={styles.container}>
      <RobotoTypography variant="body2" className={styles.sectionLabel}>
        Grid Settings
      </RobotoTypography>
      <TextField
        label="Color"
        type="color"
        size="small"
        fullWidth
        value={rgbColor}
        onChange={(e) => setRGBColor(e.target.value)}
        className={styles.field}
        InputLabelProps={{ className: styles.input }}
        InputProps={{ className: styles.input }}
        inputProps={{ className: styles.input }}
      />
      <div className={`${styles.inputContainer} ${styles.field}`}>
        <TextField
          label="Scale"
          type="number"
          size="small"
          autoComplete="off"
          value={scale}
          onChange={(e) => setScale(e.target.value)}
          error={!!scaleError}
          helperText={scaleError}
          className={styles.input}
          InputLabelProps={{ className: styles.input }}
          InputProps={{ className: styles.input }}
          inputProps={{ className: styles.input }}
        />
        <div className={styles.spacer} />
        <TextField
          label="Vertical Offset"
          type="number"
          size="small"
          autoComplete="off"
          value={verticalOffset}
          onChange={(e) => setVerticalOffset(e.target.value)}
          error={!!verticalOffsetError}
          helperText={verticalOffsetError}
          className={styles.input}
          InputLabelProps={{ className: styles.input }}
          InputProps={{ className: styles.input }}
          inputProps={{ className: styles.input }}
        />
      </div>
    </div>
  );
}
